/**
 * What an issue or a CAPA says, where it is in its workflow, what it caused, and who did what.
 *
 * THE WORKFLOW STATE IS NOT THE RECORD'S STATUS, AND THIS PANE EXISTS LARGELY TO SAY SO. The pill
 * in the page header is OBJECT_REVISION.STATUS -- DRAFT, RELEASED, the revision lifecycle every
 * species shares. A quality record ALSO moves DRAFT -> SUBMITTED -> APPROVED_FOR_ACTION -> CLOSED,
 * which is a different thing on a different column, and until this pane existed it was invisible
 * in the product: a record awaiting somebody's approval looked exactly like one nobody had sent.
 *
 * ONE DECISION WITH FOUR ANSWERS, NOT FOUR BUTTONS THAT HAPPEN TO SIT TOGETHER. The service takes
 * one path with a destination for the same reason -- four endpoints would let a client send two of
 * them -- and the legal move from any state is at most one forward and, from SUBMITTED, one back.
 * So this offers exactly the moves that are legal from where the record IS, and the refusals it
 * cannot see are still the server's to make.
 *
 * TWO OF THEM ARE REFUSALS NO PRIVILEGE CAN LIFT, and the pane says so in words rather than
 * letting somebody discover it:
 *
 *   * NOBODY APPROVES THEIR OWN RECORD. That is a fact about the PAIR (actor, record) -- a
 *     separation of duties -- so holding every grant in the tenant does not make it allowed. The
 *     button is still offered, because this component does not know who raised it; the server
 *     answers CC-QREC-SELF and that sentence is shown as it was written.
 *   * SENDING A RECORD BACK REQUIRES A REASON. Somebody submitted it and is owed an explanation,
 *     so the dialog will not send without one rather than letting the server refuse an empty box.
 *
 * `IsReadOnly` COMES FROM THE SERVER AND IS NOT RE-DERIVED HERE. It is a property of the state and
 * the record says it out loud, precisely so that two screens cannot come to disagree about whether
 * an edit box should be enabled -- which is what re-deriving it from the state name invites.
 *
 * THE DESCRIPTION IS ON THIS READ AND NOT ON THE LIST, deliberately: it may describe harm to a
 * patient, and a list of fifty issues has no business carrying fifty of them. So the detail read is
 * the only place it travels, and that is why this pane does its own fetch rather than reading the
 * record ObjectDetail already loaded.
 *
 * THE HISTORY CARRIES THE ACTOR'S ROLE AS IT WAS AT THE TIME. "Closed by a quality manager" and
 * "closed by the raiser" are different facts about the same action, and roles are granted and
 * revoked -- so the role is shown as stored and never re-resolved.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Typography,
} from "@mui/material";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey5, Grey7, Grey8, Grey17} from "../../theme/styles";

const EDIT_PRIVILEGE = "qualityRecord:edit";
const APPROVE_PRIVILEGE = "qualityRecord:approveForAction";
const CLOSE_PRIVILEGE = "qualityRecord:close";

type Record_ = {
    QualityRecordObjectUSID?: string;
    ObjectID?: string | null;
    Name?: string | null;
    Kind?: string | null;
    RecordType?: string | null;
    WorkflowState?: string | null;
    Description?: string | null;
    SubmittedDatetime?: string | null;
    ApprovedDatetime?: string | null;
    ClosedDatetime?: string | null;
    ClosedByMethod?: string | null;
    IsReadOnly?: boolean;
    CreatedDatetime?: string | null;
};

type Caused = {
    LinkUSID: string; CapaObjectUSID?: string | null; ObjectID?: string | null;
    Name?: string | null; RecordType?: string | null; WorkflowState?: string | null;
};

type Event_ = {
    Action?: string | null; ActorName?: string | null; ActorRole?: string | null;
    FromState?: string | null; ToState?: string | null; Comment?: string | null;
    Detail?: string | null; Datetime?: string | null;
};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

function when(value?: string | null): string {
    if (!value) return "—";
    const at = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 16);
    return at.toLocaleString(undefined,
                             {day: "numeric", month: "short", year: "numeric",
                              hour: "2-digit", minute: "2-digit"});
}

/** A state as a person says it, from the stored constant. */
function spell(state?: string | null): string {
    const text = String(state ?? "").replace(/_/g, " ").toLowerCase();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : "—";
}

const STATE_COLOURS: Record<string, {colour: string; background: string}> = {
    DRAFT: {colour: Grey7, background: "#F2F2F2"},
    SUBMITTED: {colour: "#1F5FA9", background: "#E8F0FA"},
    APPROVED_FOR_ACTION: {colour: "#B26A00", background: "#FDF3E3"},
    CLOSED: {colour: "#1E7B34", background: "#E8F5EA"},
};

/**
 * THE ROUTE, AS DATA. The legal moves from each state, in the order they are offered.
 *
 * DERIVED FROM ONE TABLE RATHER THAN FOUR CONDITIONS, so that a state gaining a move means adding
 * a row here instead of finding every place a button was hidden. The server holds the same table
 * and is the one that enforces it; this only decides what to OFFER.
 */
const MOVES: Record<string, {to: string; label: string; privilege: string; why: string;
                             requiresReason?: boolean}[]> = {
    DRAFT: [{to: "SUBMITTED", label: "Submit for review", privilege: EDIT_PRIVILEGE,
             why: "Sends it to whoever reviews quality records here."}],
    SUBMITTED: [
        {to: "APPROVED_FOR_ACTION", label: "Approve for action", privilege: APPROVE_PRIVILEGE,
         why: "Commits the company to doing something about it. Nobody may approve a record they "
              + "raised themselves."},
        {to: "DRAFT", label: "Send back", privilege: APPROVE_PRIVILEGE, requiresReason: true,
         why: "Returns it to the raiser. A reason is required — somebody submitted this and is "
              + "owed an explanation."},
    ],
    APPROVED_FOR_ACTION: [{to: "CLOSED", label: "Close", privilege: CLOSE_PRIVILEGE,
                           why: "Finishes with it. A closed record cannot be changed again."}],
    CLOSED: [],
};

export default function QualityRecordPane(
    {objectUSID, revisionUSID}:
    {objectUSID?: string | null; revisionUSID?: string | null}) {

    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [record, setRecord] = useState<Record_ | null>(null);
    const [caused, setCaused] = useState<Caused[]>([]);
    const [events, setEvents] = useState<Event_[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [granted, setGranted] = useState<string[]>([]);
    const [move, setMove] = useState<{to: string; label: string; why: string;
                                      requiresReason?: boolean} | null>(null);

    const load = useCallback(() => {
        if (!objectUSID) return;
        setLoading(true);
        // THE OBJECT, NOT THE REVISION. A quality record is raised once and worked -- it is not
        // revised -- so everything about it hangs off the logical object, and passing the revision
        // USID here would find nothing and read as "not found".
        api.get("t1/v1/quality_record_details", {params: {QualityRecordObjectUSID: objectUSID}})
            .then((response: any) => {
                const message = response?.data?.PlatformMessage ?? {};
                setRecord(message?.QualityRecord ?? null);
                setCaused(listOf<Caused>(message?.ListOfCausedCapa?.CausedCapa));
                setEvents(listOf<Event_>(message?.ListOfEvent?.Event));
                setError(null);
            })
            .catch((problem: any) => {
                setRecord(null);
                setError(problem?.response?.data?.Cause
                         ?? "This quality record could not be read.");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID]);

    useEffect(() => { load(); }, [load]);

    // ASKED OF THE RECORD, like every other pane: a per-share grant or deny narrows a privilege
    // for ONE record, and a global answer would offer a decision this person cannot take.
    useEffect(() => {
        if (!revisionUSID) return;
        let cancelled = false;
        api.get("t1/v1/object_access", {params: {USID: revisionUSID}})
            .then((response: any) => {
                if (!cancelled) setGranted(response?.data?.EffectivePrivileges ?? []);
            })
            .catch(() => { if (!cancelled) setGranted([]); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID]);

    if (loading && !record && !error) {
        return <Box sx={{display: "flex", gap: "10px", alignItems: "center", padding: "20px 0"}}>
            <CircularProgress size={18}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Reading the record…</Typography>
        </Box>;
    }

    if (error) {
        return <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.6, maxWidth: 700,
                                paddingTop: "8px"}}>{error}</Typography>;
    }

    if (!record) return null;

    const state = String(record.WorkflowState ?? "");
    const colours = STATE_COLOURS[state] ?? {colour: Grey7, background: "#F2F2F2"};
    const offered = (MOVES[state] ?? []).filter(one => granted.includes(one.privilege));
    const withheld = (MOVES[state] ?? []).filter(one => !granted.includes(one.privilege));

    return (
        <Box sx={{paddingTop: "6px", maxWidth: 980}}>

            {/* ── where it is, which is NOT the pill in the header ──────────────────────────── */}
            <Box sx={{display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap",
                      marginBottom: "6px"}}>
                <Box sx={{background: colours.background, color: colours.colour,
                          borderRadius: "4px", padding: "6px 14px", fontSize: 15,
                          fontWeight: 600}}>
                    {spell(state)}
                </Box>
                {record.RecordType && (
                    <Chip size="small" label={record.RecordType}
                          sx={{height: 22, fontSize: 12}}/>
                )}
                <Box sx={{flex: 1}}/>
                {offered.map(one => (
                    <Button key={one.to} size="small"
                            variant={one.to === "DRAFT" ? "outlined" : "contained"}
                            onClick={() => setMove(one)}>
                        {one.label}
                    </Button>
                ))}
            </Box>

            {/* THE WORKFLOW IS NOT THE REVISION LIFECYCLE, and the two words in the two pills are
                close enough to be read as the same thing. Said once, here. */}
            <Typography sx={{fontSize: 12, color: Grey5, lineHeight: 1.6, maxWidth: 760,
                             marginBottom: "16px"}}>
                This is the record's own workflow, which is not the revision status in the header
                above: a record moves draft → submitted → approved for action → closed as people
                act on it.
            </Typography>

            {/* WHAT THEY CANNOT DO, NAMED. Offering nothing and explaining nothing leaves somebody
                looking for a button that is not there. */}
            {!!withheld.length && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 760,
                                 marginBottom: "16px"}}>
                    {withheld.length === 1
                        ? `You do not hold the privilege to ${withheld[0].label.toLowerCase()} `
                          + `this record.`
                        : `You do not hold the privileges to act on this record from here.`}
                </Typography>
            )}

            {state === "CLOSED" && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 760,
                                 marginBottom: "16px"}}>
                    This record is closed{record.ClosedByMethod
                        ? ` (${String(record.ClosedByMethod).replace(/_/g, " ").toLowerCase()})`
                        : ""}, so nothing on it can be changed. What it says is what the company
                    concluded.
                </Typography>
            )}

            {/* ── what it says ──────────────────────────────────────────────────────────────── */}
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "8px"}}>
                What was raised
            </Typography>
            <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "12px 16px",
                      marginBottom: "18px"}}>
                {record.Description
                    ? <Typography sx={{fontSize: 13, color: Grey17, lineHeight: 1.7,
                                       whiteSpace: "pre-wrap"}}>
                          {record.Description}
                      </Typography>
                    : <Typography sx={{fontSize: 13, color: Grey5, lineHeight: 1.6}}>
                          Nothing was written here. An issue with no description is a record
                          nobody can act on from the record alone.
                      </Typography>}
                <Box sx={{display: "flex", gap: "28px", flexWrap: "wrap", marginTop: "14px",
                          paddingTop: "12px", borderTop: `1px solid ${Grey2}`}}>
                    <Fact label="Raised" value={when(record.CreatedDatetime)}/>
                    <Fact label="Submitted" value={when(record.SubmittedDatetime)}/>
                    <Fact label="Approved" value={when(record.ApprovedDatetime)}/>
                    <Fact label="Closed" value={when(record.ClosedDatetime)}/>
                </Box>
            </Box>

            {/* ── what came of it ───────────────────────────────────────────────────────────── */}
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "8px"}}>
                What this caused
            </Typography>
            {caused.length === 0 ? (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 760,
                                 marginBottom: "18px"}}>
                    Nothing has been raised from this record. A corrective action can only be raised
                    from an issue once it is approved for action — raising one against something
                    nobody has decided to act on would put the decision after the work.
                </Typography>
            ) : (
                <Box sx={{marginBottom: "18px"}}>
                    {caused.map(one => (
                        <Box key={one.LinkUSID}
                             sx={{border: `1px solid ${Grey2}`, borderRadius: "6px",
                                  padding: "10px 14px", marginBottom: "8px",
                                  display: "flex", alignItems: "center", gap: "12px"}}>
                            <Typography sx={{fontSize: 13, color: Blue3, fontWeight: 600}}>
                                {one.ObjectID || "—"}
                            </Typography>
                            <Typography sx={{fontSize: 13, color: Grey17, overflow: "hidden",
                                             textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                {one.Name || ""}
                            </Typography>
                            <Box sx={{flex: 1}}/>
                            <Typography sx={{fontSize: 12, color: Grey7}}>
                                {spell(one.WorkflowState)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* ── who did what, and as what ─────────────────────────────────────────────────── */}
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "8px"}}>
                History
            </Typography>
            {events.length === 0 ? (
                <Typography sx={{fontSize: 13, color: Grey7}}>Nothing has happened to it yet.</Typography>
            ) : (
                <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", overflow: "hidden"}}>
                    {events.map((one, index) => (
                        <Box key={`${one.Datetime}-${index}`}
                             sx={{padding: "10px 14px", borderBottom: `1px solid ${Grey2}`,
                                  "&:last-of-type": {borderBottom: "none"}}}>
                            <Box sx={{display: "flex", gap: "10px", alignItems: "baseline",
                                      flexWrap: "wrap"}}>
                                <Typography sx={{fontSize: 13, color: Grey17, fontWeight: 600}}>
                                    {spell(one.Action)}
                                </Typography>
                                {one.FromState && one.ToState && (
                                    <Typography sx={{fontSize: 12, color: Grey5}}>
                                        {spell(one.FromState)} → {spell(one.ToState)}
                                    </Typography>
                                )}
                                <Box sx={{flex: 1}}/>
                                <Typography sx={{fontSize: 12, color: Grey5}}>
                                    {when(one.Datetime)}
                                </Typography>
                            </Box>
                            <Typography sx={{fontSize: 13, color: Grey7, marginTop: "2px"}}>
                                {/* THE ROLE AS IT WAS AT THE TIME. Re-resolving it from the roles
                                    somebody holds now answers a different question. */}
                                {one.ActorName || "Unknown person"}
                                {one.ActorRole ? `, ${one.ActorRole} at the time` : ""}
                            </Typography>
                            {one.Comment && (
                                <Typography sx={{fontSize: 13, color: Grey17, marginTop: "6px",
                                                 lineHeight: 1.6, whiteSpace: "pre-wrap"}}>
                                    “{one.Comment}”
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            {move && (
                <MoveDialog move={move} objectUSID={objectUSID!}
                            onClose={() => setMove(null)}
                            onDone={() => { setMove(null); load(); }}/>
            )}
        </Box>
    );
}

function Fact({label, value}: {label: string; value: React.ReactNode}) {
    return (
        <Box>
            <Typography sx={{fontSize: 11, color: Grey5, textTransform: "uppercase",
                             letterSpacing: "0.04em"}}>
                {label}
            </Typography>
            <Typography sx={{fontSize: 13, color: Grey17, marginTop: "2px"}}>{value}</Typography>
        </Box>
    );
}

/**
 * One move, with its comment.
 *
 * A REASON IS REQUIRED GOING BACK AND OPTIONAL EVERYWHERE ELSE, and the difference is not a
 * validation detail: somebody submitted this record and is owed an explanation for its return. The
 * button stays disabled rather than letting the server refuse an empty box, because the person has
 * already decided to send it back and a refusal at that point teaches them nothing.
 *
 * THE COMMENT IS WORTH WRITING ON A CLOSE even though nothing requires it -- it is the only place
 * "what was actually done about this" is recorded, and the helper text says so.
 */
function MoveDialog({move, objectUSID, onClose, onDone}: {
    move: {to: string; label: string; why: string; requiresReason?: boolean};
    objectUSID: string; onClose: () => void; onDone: () => void;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const send = async () => {
        setSaving(true);
        setProblem(null);
        try {
            await api.post("t1/v1/quality_record_transitions", {
                QualityRecordObjectUSID: objectUSID,
                To: move.to,
                Comment: comment || undefined,
            });
            onDone();
        } catch (caught: any) {
            // THE SERVER'S OWN SENTENCE, and on this path it is the whole point. CC-QREC-SELF
            // explains that approving your own record is a separation of duties and no grant makes
            // it allowed -- a message this component could not write, and the one a person needs.
            setProblem(caught?.response?.data?.Cause ?? "That could not be done.");
            setSaving(false);
        }
    };

    const ready = !move.requiresReason || comment.trim().length > 0;

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>{move.label}</DialogTitle>
            <DialogContent>
                <Box sx={{display: "flex", flexDirection: "column", gap: "14px",
                          paddingTop: "4px"}}>
                    <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>
                        {move.why}
                    </Typography>
                    <TextField label={move.requiresReason ? "Reason" : "Comment"}
                               size="small" fullWidth multiline minRows={3} value={comment}
                               onChange={event => setComment(event.target.value)}
                               helperText={move.requiresReason
                                   ? "Required. It is recorded against the record and the person "
                                     + "who submitted it will read it."
                                   : move.to === "CLOSED"
                                       ? "Optional, and worth writing — this is where what was "
                                         + "actually done gets recorded."
                                       : "Optional."}/>
                    {problem && (
                        <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.6}}>
                            {problem}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose}>Cancel</Button>
                <Button size="small" variant="contained" disabled={!ready || saving}
                        onClick={send}>
                    {saving ? "Working…" : move.label}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
